import './App.css';
import { DriversPage } from "./pages/drivers/DriversPage";
import { Navigate, Route, Routes } from 'react-router-dom';
import { GuardRoute } from './components/common/GuardRoute';
import { observer } from 'mobx-react';
import { DriverDetailsPage } from './pages/drivers/DriverDetailsPage';
import LoginPage from './pages/auth/LoginPage';
import SetPasswordPage from './pages/auth/SetPasswordPage';
import RegisterPage from './pages/auth/RegisterPage';
import PasswordRecoveryPage from './pages/auth/PasswordRecoveryPage';
import { NewMedicalCheckupsPage } from './pages/medicalCheckups/NewMedicalCheckupsPage';
import { ROUTES } from './constants/routes'
import { MedicalCheckupsHistoryPage } from './pages/medicalCheckups/MedicalCheckupsHistoryPage';
import { MedicalCheckupDetailsPage } from './pages/medicalCheckups/MedicalCheckupDetailsPage';
import { MedicalCheckupsJournalPage } from './pages/medicalCheckups/MedicalCheckupsJournalPage';
import NotFoundPage from './pages/auth/NotFoundPage';
import { featureTogglesService } from './services/featureTogglesService';
import { TerminalsPage } from './pages/terminals/TerminalsPage';
import { TerminalDetailsPage } from './pages/terminals/TerminalDetailsPage';
import { DispatchersPage } from './pages/dispatchers/DispatchersPage';
import { DispatcherDetailsPage } from './pages/dispatchers/DispatcherDetailsPage';
import { CompaniesPage } from './pages/companies/CompaniesPage';
import { AddCompanyPage } from './pages/companies/AddCompanyPage';
import { CompanyDetailsPage } from './pages/companies/CompanyDetailsPage';
import { MedicDetailsPage } from './pages/medics/MedicDetailsPage';
import { MedicsPage } from './pages/medics/MedicsPage';
import { ShowForPermission } from './components/common/permissions/ShowForPermission';
import { CategoryType } from './models/enums/categoryType';
import { PermissionType } from './models/enums/permissionType';
import { RiskGroupPage } from './pages/analytics/RiskGroupPage';
import { DirectionsPage } from './pages/analytics/DirectionsPage';
import { MedicWorkloadPage } from './pages/analytics/MedicWorkloadPage';
import { HomePage } from './pages/home/HomePage';
import { ShowForRole } from './components/common/permissions/ShowForRole';
import { RoleType } from './models/enums/roleType';
import { WaybillsPage } from './pages/waybills/WaybillsPage';

export const App = observer(() => {
    const isRegistrationEnabled = featureTogglesService.isRegistrationEnabled();

    return (
        <Routes>
            <Route path={ROUTES.auth.notFound} element={<NotFoundPage />} />
            <Route path={ROUTES.auth.login} element={<LoginPage />} />
            <Route path={ROUTES.auth.setPassword} element={<SetPasswordPage />} />
            {isRegistrationEnabled && <Route path={ROUTES.auth.register} element={<RegisterPage />} />}
            <Route path={ROUTES.auth.passwordRecovery} element={<PasswordRecoveryPage />} />

            <Route path={ROUTES.drivers.list} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Drivers} permission={PermissionType.ReadCatalog}>
                        <DriversPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.drivers.idPath} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Drivers} permission={PermissionType.ReadById}>
                        <DriverDetailsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />

            <Route path={ROUTES.medicalCheckups.listOfNew} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.MedicalCheckups} permission={PermissionType.ReadCatalog}>
                        <NewMedicalCheckupsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.medicalCheckups.history} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.MedicalCheckups} permission={PermissionType.ReadCatalog}>
                        <MedicalCheckupsHistoryPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.medicalCheckups.journal} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.MedicalJournals} permission={PermissionType.ReadCatalog}>
                        <MedicalCheckupsJournalPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.medicalCheckups.idPath} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.MedicalCheckups} permission={PermissionType.ReadById}>
                        <MedicalCheckupDetailsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />

            <Route path={ROUTES.terminals.list} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Terminals} permission={PermissionType.ReadCatalog}>
                        <TerminalsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.terminals.idPath} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Terminals} permission={PermissionType.ReadById}>
                        <TerminalDetailsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />

            <Route path={ROUTES.dispatchers.list} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Dispatchers} permission={PermissionType.ReadCatalog}>
                        <DispatchersPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.dispatchers.idPath} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Dispatchers} permission={PermissionType.ReadById}>
                        <DispatcherDetailsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />

            <Route path={ROUTES.clients.list} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Companies} permission={PermissionType.ReadCatalog}>
                        <CompaniesPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.clients.add} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Companies} permission={PermissionType.Add}>
                        <AddCompanyPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.clients.idPath} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Companies} permission={PermissionType.ReadById}>
                        <CompanyDetailsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />

            <Route path={ROUTES.medics.list} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Medics} permission={PermissionType.ReadCatalog}>
                        <MedicsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.medics.idPath} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Medics} permission={PermissionType.ReadById}>
                        <MedicDetailsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.analytics.directions} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Analytics} permission={PermissionType.ReadCatalog}>
                        <DirectionsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.analytics.riskGroup} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.Analytics} permission={PermissionType.ReadCatalog}>
                        <RiskGroupPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path={ROUTES.analytics.medicWorkload} element={
                <GuardRoute>
                    <ShowForRole role={RoleType.Admin}>
                        <MedicWorkloadPage />
                    </ShowForRole>
                </GuardRoute>
            } />
            <Route path={ROUTES.home} element={
                <GuardRoute>
                    <HomePage />
                </GuardRoute>
            } />
            <Route path={ROUTES.waybills.list} element={
                <GuardRoute>
                    <ShowForPermission catalog={CategoryType.MedicalCheckups} permission={PermissionType.ReadCatalog}>
                        <WaybillsPage />
                    </ShowForPermission>
                </GuardRoute>
            } />
            <Route path="/waybills/epl" element={<Navigate to={ROUTES.waybills.list} replace />} />
            <Route path="*" element={<Navigate to={ROUTES.auth.login} replace />} />
        </Routes>
    )
})